import { PageTypeEnum } from '../translation_namespace/page_type_namespace';
import { COMPONENT_SCHEMA_REGISTRY } from './ComponentSchemaRegistry';
import { ComponentId, WebsiteConfig, isComponentRef } from './WebsiteTypes';

// ─── Types ────────────────────────────────────────────────────────────

/**
 * One establishment photo resolved for a collection binding (`{{pro:pictures}}`).
 * Mirrors the shape the website carousel renderer consumes (PictureValue): a
 * displayable `url`/`original_url`, optionally per-size signed URLs and a crop.
 * On the public SSR path only the raw `url` (S3 key) and `crop` are set — the
 * per-size signing happens later via `transformToSchema`/`enrichComponentPictures`.
 */
export interface BindingPicture {
  url?: string;
  /**
   * Raw S3 key of the photo (app/builder path only, where `url` is the signed, already
   * cropped `preview_url`). It is what must be PERSISTED when a binding is resolved back
   * to a manual value (unbind): storing the signed URL as `url` would make the backend
   * re-sign it as if it were a key and apply the crop a second time.
   */
  raw_url?: string;
  original_url?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  crop?: { x: number; y: number; width: number; height: number };
}

export interface ProfessionalBindingData {
  name: string;
  /** Présentation/description riche de l'établissement (HTML sanitisé). Resolves `{{pro:description}}`. */
  description?: string;
  legal_name?: string;
  siret?: string;
  phone_number?: string;
  email_contact: string;
  address?: { formatted_address?: string; city?: string; zip?: string };
  logo_url?: string;
  /** Establishment photo gallery. Resolves the `{{pro:pictures}}` collection binding (e.g. hero carousel). */
  pictures?: BindingPicture[];
  external_urls?: { type: string; url: string; name?: string }[];
  /** Absolute URL of the pro's public booking page (`{origin}/book/{slug}`). Resolves `{{pro:booking}}`. */
  booking_url?: string;
  /**
   * Page publique d'achat de carte cadeau (`{origin}/gift-card/{slug}`). Résout `{{pro:gift_card}}`.
   * Absente quand le pro n'a pas activé la vente de cartes cadeaux : un bouton qui pointe dessus
   * n'est alors pas rendu en public.
   */
  gift_card_url?: string;
  /** Lien « écrire un avis » de la fiche Google du pro. Résout `{{pro:google_review}}` (vide sans fiche). */
  google_review_url?: string;
}

export interface LinkBindingData {
  pages: Record<string, string>;
  categories: Record<string, { slug: string; servicesPageSlug: string }>;
  prestations: Record<string, { slug: string; categorySlug: string; servicesPageSlug: string }>;
  news?: Record<string, { slug: string; blogPageSlug: string }>;
}

// ─── Pattern & helpers ────────────────────────────────────────────────

export const BINDING_PATTERN = /\{\{([a-z0-9_:-]+)\}\}/g;

export function isBinding(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  BINDING_PATTERN.lastIndex = 0;
  return BINDING_PATTERN.test(value);
}

export function getBindingKey(value: string): string | null {
  BINDING_PATTERN.lastIndex = 0;
  const match = BINDING_PATTERN.exec(value);
  return match ? match[1] : null;
}

export function isLinkBinding(key: string): boolean {
  return (
    key.startsWith('page:') || key.startsWith('category:') || key.startsWith('prestation:') || key.startsWith('news:')
  );
}

// ─── Bindable fields registry ─────────────────────────────────────────

export interface BindableFieldMeta {
  label: string;
  group: string;
  /**
   * `image_collection` binds an ARRAY field (e.g. the hero carousel `imagesUrl`)
   * to a list of pictures, resolved via COLLECTION_BINDING_RESOLVERS rather than
   * the scalar BINDING_RESOLVERS. `string`/`image` bind a single scalar value.
   */
  type: 'string' | 'image' | 'image_collection';
  /** When true, the binding is masked in the public SSR output and must be fetched via the captcha-protected reveal endpoint. */
  protected?: boolean;
  /** Placeholder shown in the public SSR output while the real value is being revealed. */
  protectedPlaceholder?: string;
}

export const BINDABLE_FIELDS: Record<string, BindableFieldMeta> = {
  'pro:name': { label: 'BINDING_PRO_NAME', group: 'BINDING_GROUP_GENERAL', type: 'string' },
  'pro:description': { label: 'BINDING_PRO_DESCRIPTION', group: 'BINDING_GROUP_GENERAL', type: 'string' },
  'pro:legal_name': { label: 'BINDING_PRO_LEGAL_NAME', group: 'BINDING_GROUP_GENERAL', type: 'string' },
  'pro:siret': { label: 'BINDING_PRO_SIRET', group: 'BINDING_GROUP_GENERAL', type: 'string' },
  'pro:phone': {
    label: 'BINDING_PRO_PHONE',
    group: 'BINDING_GROUP_CONTACT',
    type: 'string',
    protected: true,
    protectedPlaceholder: '•• •• •• •• ••',
  },
  'pro:email': {
    label: 'BINDING_PRO_EMAIL',
    group: 'BINDING_GROUP_CONTACT',
    type: 'string',
    protected: true,
    protectedPlaceholder: '••••••@••••••',
  },
  'pro:address:full': { label: 'BINDING_PRO_FULL_ADDRESS', group: 'BINDING_GROUP_ADDRESS', type: 'string' },
  'pro:address:city': { label: 'BINDING_PRO_CITY', group: 'BINDING_GROUP_ADDRESS', type: 'string' },
  'pro:address:zip': { label: 'BINDING_PRO_ZIP', group: 'BINDING_GROUP_ADDRESS', type: 'string' },
  'pro:logo': { label: 'BINDING_PRO_LOGO', group: 'BINDING_GROUP_MEDIA', type: 'image' },
  'pro:pictures': { label: 'BINDING_PRO_PICTURES', group: 'BINDING_GROUP_MEDIA', type: 'image_collection' },
  'pro:first_picture': { label: 'BINDING_PRO_FIRST_PICTURE', group: 'BINDING_GROUP_MEDIA', type: 'image' },
};

export function isCollectionBindableField(key: string): boolean {
  return BINDABLE_FIELDS[key]?.type === 'image_collection';
}

export function isProtectedBinding(key: string): boolean {
  return BINDABLE_FIELDS[key]?.protected === true;
}

/**
 * Placeholders emitted for a protected binding on a PLAIN-TEXT field (see
 * `maskProtectedBindingsPlain`): those fields carry no `data-protect` DOM marker, so the
 * client-side reveal can only find them by matching their placeholder text. Keep the
 * placeholders distinctive and unique per key — they are matched literally in the DOM.
 */
export const PROTECTED_PLACEHOLDERS: { key: string; placeholder: string }[] = Object.entries(BINDABLE_FIELDS)
  .filter(([, meta]): boolean => meta.protected === true && Boolean(meta.protectedPlaceholder))
  .map(([key, meta]): { key: string; placeholder: string } => ({
    key,
    placeholder: meta.protectedPlaceholder as string,
  }));

// ─── Pro data mapping ─────────────────────────────────────────────────

/** Adresse d'un pro (`professional.address`, Json) au format des bindings `{{pro:address:*}}`. */
export function toBindingAddress(raw: unknown): ProfessionalBindingData['address'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const address = raw as { formatted_address?: string; city?: string; postal_code?: string; zip?: string };
  return { formatted_address: address.formatted_address, city: address.city, zip: address.postal_code ?? address.zip };
}

/**
 * Build ProfessionalBindingData from a raw pro info object (API response shape).
 * Works with both the app-side ProInfosResponse (logo is array) and public profile (logo is object).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pro info shape varies between app and public API
export function buildProfessionalBindingData(
  proInfo: Record<string, any>,
  // Origin used to build the public booking URL (`{origin}/book/{slug}`).
  // The booking page lives on the app origin, not the pro's website domain.
  bookingBaseUrl?: string,
): ProfessionalBindingData {
  const logoRaw = proInfo.logo;
  const logoUrl: string | undefined = Array.isArray(logoRaw)
    ? logoRaw[0]?.preview_url
    : (logoRaw?.preview_url ?? undefined);

  // Establishment photos (CroppableImageResponse[]) → renderable PictureValue-ish
  // objects for the carousel. IMPORTANT: `preview_url` is the CROPPED image
  // (Thumbor crop_prefix) while `original_url` is the UNCROPPED full original.
  // `pictureSrc` prefers `original_url` over `url`, so we must NOT pass the
  // uncropped original here — otherwise the carousel shows the full image and
  // ignores the pro's crop. Use the cropped `preview_url` as the only source.
  // (The public SSR path builds this differently — raw keys re-signed with the
  // crop on the carousel's hero sizes; see professional-public-data.service.)
  const picturesRaw: unknown = proInfo.pictures;
  const pictures: BindingPicture[] | undefined = Array.isArray(picturesRaw)
    ? picturesRaw
        .map((raw): BindingPicture => {
          const p = (raw ?? {}) as {
            preview_url?: string;
            original_url?: string;
            url?: string;
            crop?: BindingPicture['crop'];
          };
          const cropped: string | undefined = p.preview_url ?? p.original_url ?? p.url;
          return { url: cropped, raw_url: p.url, crop: p.crop };
        })
        .filter((p): boolean => Boolean(p.url))
    : undefined;

  const slug: string | undefined = proInfo.slug ?? undefined;
  const origin = bookingBaseUrl?.replace(/\/$/, '');
  const bookingUrl: string | undefined = slug && origin ? `${origin}/book/${slug}` : undefined;
  // Aperçu du builder : le lien est toujours construit. À l'envoi, le backend ne le pose que si la
  // vente de cartes cadeaux est réellement active (cf. `pro_campaign.service`).
  const giftCardUrl: string | undefined = slug && origin ? `${origin}/gift-card/${slug}` : undefined;

  return {
    name: proInfo.name ?? '',
    description: proInfo.description ?? undefined,
    legal_name: proInfo.legal_name ?? undefined,
    siret: proInfo.siret_number ?? proInfo.siret ?? undefined,
    phone_number: proInfo.phone_number ?? undefined,
    email_contact: proInfo.email_contact ?? '',
    address: toBindingAddress(proInfo.address),
    logo_url: logoUrl,
    pictures,
    external_urls: proInfo.external_urls ?? undefined,
    booking_url: bookingUrl,
    gift_card_url: giftCardUrl,
  };
}

// ─── Resolvers ────────────────────────────────────────────────────────

// Collection bindings resolve an ARRAY field (not a scalar string). Stored on a
// component as a single-element array whose item carries the placeholder
// (`[{ url: '{{pro:pictures}}' }]`), kept schema-valid against Type.Array(PictureSchema).
const COLLECTION_BINDING_RESOLVERS: Record<string, (data: ProfessionalBindingData) => unknown[]> = {
  'pro:pictures': (d): BindingPicture[] => d.pictures ?? [],
};

export function isCollectionBinding(key: string): boolean {
  return key in COLLECTION_BINDING_RESOLVERS;
}

/**
 * Picture-object bindings resolve a SINGLE-image field (`PictureSchema`), stored as
 * `{ url: '{{pro:first_picture}}' }` — the same wrapper `handleBindField` writes for
 * `pro:logo`.
 *
 * They are NOT scalar `BINDING_RESOLVERS`: a scalar resolver returns a string that is
 * substituted into `url`, which would drop the photo's `crop` and render the pro's
 * establishment picture uncropped. These resolvers replace the WHOLE object instead, so
 * `{ url, crop }` travels together — the crop prefix is then applied by
 * `transformToSchema` (public SSR, where `url` is still a raw S3 key) or is already
 * baked into the signed `preview_url` (app/builder path, cf. buildProfessionalBindingData).
 */
const PICTURE_OBJECT_BINDING_RESOLVERS: Record<string, (data: ProfessionalBindingData) => BindingPicture | undefined> =
  {
    'pro:first_picture': (d): BindingPicture | undefined => d.pictures?.[0],
  };

export function isPictureObjectBinding(key: string): boolean {
  return key in PICTURE_OBJECT_BINDING_RESOLVERS;
}

/**
 * If `val` is a picture-object binding (`{ url: '{{pro:first_picture}}' }`), return the
 * binding key; otherwise null. Mirrors `extractCollectionBindingKey` for single images.
 */
export function extractPictureObjectBindingKey(val: unknown): string | null {
  if (val === null || typeof val !== 'object' || Array.isArray(val)) return null;
  const url = (val as { url?: unknown }).url;
  if (typeof url !== 'string') return null;
  const key = getBindingKey(url);
  return key && isPictureObjectBinding(key) ? key : null;
}

/**
 * Resolve a picture-object binding key to the picture it points at. `undefined` when the
 * key isn't one OR when the pro has no matching photo: in both cases the caller clears
 * the field rather than leaving an unresolved placeholder (an empty object would reach
 * `<img>` with no `src`).
 */
export function resolvePictureObjectBindingValue(
  key: string,
  data: ProfessionalBindingData,
): BindingPicture | undefined {
  return PICTURE_OBJECT_BINDING_RESOLVERS[key]?.(data);
}

/**
 * Resolve a collection-binding key (e.g. `pro:pictures`) to its array value.
 * Returns null if the key is not a collection binding. Exposed so resolvers that
 * walk a config themselves (e.g. the builder preview) can expand collections
 * without re-implementing the registry.
 */
export function resolveCollectionBindingValue(key: string, data: ProfessionalBindingData): unknown[] | null {
  const resolver = COLLECTION_BINDING_RESOLVERS[key];
  return resolver ? resolver(data) : null;
}

/**
 * If `val` is a collection-binding array (`[{ url: '{{pro:pictures}}' }]`),
 * return the binding key; otherwise null. Used to detect bound array fields
 * both at resolution time and in the builder UI.
 */
export function extractCollectionBindingKey(val: unknown): string | null {
  if (!Array.isArray(val) || val.length === 0) return null;
  const first = val[0];
  if (first !== null && typeof first === 'object') {
    const url = (first as { url?: unknown }).url;
    if (typeof url === 'string') {
      const key = getBindingKey(url);
      if (key && isCollectionBinding(key)) return key;
    }
  }
  return null;
}

const BINDING_RESOLVERS: Record<string, (data: ProfessionalBindingData) => string> = {
  'pro:name': (d) => d.name,
  'pro:description': (d) => d.description ?? '',
  'pro:legal_name': (d) => d.legal_name ?? d.name,
  'pro:siret': (d) => d.siret ?? '',
  'pro:phone': (d) => d.phone_number ?? '',
  'pro:email': (d) => d.email_contact,
  'pro:address:full': (d) => d.address?.formatted_address ?? '',
  'pro:address:city': (d) => d.address?.city ?? '',
  'pro:address:zip': (d) => d.address?.zip ?? '',
  'pro:logo': (d) => d.logo_url ?? '',
  // Forme SCALAIRE (URL) de la première photo, pour les champs image des emails (`imageUrl`,
  // `logoUrl`). Le site web, lui, stocke `{ url: '{{pro:first_picture}}' }` et passe par
  // `PICTURE_OBJECT_BINDING_RESOLVERS` (objet entier, crop conservé) AVANT d'atteindre ce résolveur.
  'pro:first_picture': (d) => d.pictures?.[0]?.url ?? '',
  'pro:booking': (d) => d.booking_url ?? '',
  'pro:gift_card': (d) => d.gift_card_url ?? '',
  'pro:google_review': (d) => d.google_review_url ?? '',
};

function resolveLinkBinding(key: string, linkData: LinkBindingData): string | null {
  if (key.startsWith('page:')) {
    const pageId = key.slice(5);
    return linkData.pages[pageId] ?? null;
  }
  if (key.startsWith('category:')) {
    const catId = key.slice(9);
    const cat = linkData.categories[catId];
    return cat ? `${cat.servicesPageSlug}/${cat.slug}` : null;
  }
  if (key.startsWith('prestation:')) {
    const prestaId = key.slice(11);
    const presta = linkData.prestations[prestaId];
    return presta ? `${presta.servicesPageSlug}/${presta.categorySlug}/${presta.slug}` : null;
  }
  if (key.startsWith('news:')) {
    const newsId = key.slice(5);
    const item = linkData.news?.[newsId];
    return item ? `${item.blogPageSlug}/${item.slug}` : null;
  }
  return null;
}

export function resolveBindingValue(
  value: string,
  data: ProfessionalBindingData,
  linkData?: LinkBindingData,
  baseUrl?: string,
  resolvedLinks?: Record<string, string>,
): string {
  BINDING_PATTERN.lastIndex = 0;
  return value.replace(BINDING_PATTERN, (_, key: string) => {
    const resolver = BINDING_RESOLVERS[key];
    if (resolver) return resolver(data);
    if (resolvedLinks && key in resolvedLinks) {
      const url = resolvedLinks[key];
      return baseUrl ? baseUrl + url : url;
    }
    if (linkData) {
      const linkResult = resolveLinkBinding(key, linkData);
      if (linkResult !== null) return baseUrl ? baseUrl + linkResult : linkResult;
    }
    return `{{${key}}}`;
  });
}

// ─── Available internal links (shared between website & email builders) ─

export interface InternalLinkItem {
  key: string; // e.g. 'page:abc', 'category:12', 'prestation:45', 'news:67'
  label: string;
  group: 'page' | 'category' | 'prestation' | 'news';
}

export interface InternalLinkOptions {
  pages: Array<{ id: string; title: string; slug: string; type: string }>;
  categories: Array<{ catalog_category_id: number; name: string; slug: string }>;
  prestations: Array<{ prestation_id: number; name: string; slug: string; category_id?: number }>;
  news?: Array<{ pro_news_id: number; title: string; slug: string }>;
}

export function getAvailableInternalLinks(options: InternalLinkOptions): InternalLinkItem[] {
  const items: InternalLinkItem[] = [];

  for (const page of options.pages) {
    items.push({ key: `page:${page.id}`, label: page.title, group: 'page' });
  }

  const hasServicesPage = options.pages.some((p) => p.type === 'services');
  if (hasServicesPage) {
    for (const cat of options.categories) {
      items.push({
        key: `category:${cat.catalog_category_id}`,
        label: cat.name,
        group: 'category',
      });
    }

    for (const presta of options.prestations) {
      const cat = options.categories.find((c) => c.catalog_category_id === presta.category_id);
      items.push({
        key: `prestation:${presta.prestation_id}`,
        label: cat ? `${cat.name} > ${presta.name}` : presta.name,
        group: 'prestation',
      });
    }
  }

  const hasBlogPage = options.pages.some((p) => p.type === 'blog');
  if (hasBlogPage && options.news) {
    for (const article of options.news) {
      items.push({
        key: `news:${article.pro_news_id}`,
        label: article.title,
        group: 'news',
      });
    }
  }

  return items;
}

// ─── Deep resolution on a full config ─────────────────────────────────

function resolveValueDeep(
  val: unknown,
  data: ProfessionalBindingData,
  linkData?: LinkBindingData,
  resolvedLinks?: Record<string, string>,
): unknown {
  if (typeof val === 'string' && isBinding(val)) {
    return resolveBindingValue(val, data, linkData, undefined, resolvedLinks);
  }
  if (Array.isArray(val)) {
    // Collection binding: the whole array is replaced by the resolved list
    // (e.g. `{{pro:pictures}}` → establishment photos) instead of resolving
    // each item's scalar placeholders.
    const collectionKey = extractCollectionBindingKey(val);
    if (collectionKey) {
      const resolver = COLLECTION_BINDING_RESOLVERS[collectionKey];
      if (resolver) return resolver(data);
    }
    return val.map((item: unknown) => resolveValueDeep(item, data, linkData, resolvedLinks));
  }
  if (val !== null && typeof val === 'object') {
    // Picture-object binding: the whole object is replaced by the resolved photo
    // (e.g. `{{pro:first_picture}}` → first establishment photo) so its `crop`
    // survives — resolving `url` as a scalar would strip it. No photo → `undefined`
    // (NOT `{}`): renderers test the field for truthiness, so an empty object would
    // reach `<img>` with no `src` instead of dropping the media block entirely.
    const pictureKey = extractPictureObjectBindingKey(val);
    if (pictureKey) {
      return resolvePictureObjectBindingValue(pictureKey, data);
    }
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      result[k] = resolveValueDeep(v, data, linkData, resolvedLinks);
    }
    return result;
  }
  return val;
}

/**
 * Resolves every binding (scalar, collection, picture-object, link) inside one component's
 * `values`. Single walker shared by the public SSR path (`resolveBindingsPublic`) and the
 * builder preview — `resolvedLinks` is the builder's pre-resolved `{ 'page:x': url }` map,
 * used instead of `linkData` when the caller has already computed the public URLs.
 */
export function resolveValuesRecord(
  values: Record<string, unknown>,
  data: ProfessionalBindingData,
  linkData?: LinkBindingData,
  resolvedLinks?: Record<string, string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(values)) {
    result[key] = resolveValueDeep(val, data, linkData, resolvedLinks);
  }
  return result;
}

type WebsitePage = WebsiteConfig['pages'][number];

/** Maps the values of every component of a page, then of ALL its sub-pages, recursively. */
type ComponentValuesMapper = (values: Record<string, unknown>, componentId: string) => Record<string, unknown>;

/**
 * Applies `mapValues` to every component of `page` AND of its sub-pages (`children`).
 *
 * Sub-pages carry their own components (v1-migrated "réalisations", nested custom pages)
 * and are served by the very same SSR path as root pages. Walking only `config.pages`
 * left every `{{...}}` of a sub-page unresolved in the published HTML — visitors saw the
 * raw token (`{{pro:name}}` in a quote, `href="{{category:18}}"` on a CTA).
 */
function mapPageComponentsDeep(page: WebsitePage, mapValues: ComponentValuesMapper): WebsitePage {
  const components = page.components.map((component) => {
    // Refs don't have values — skip them
    if (isComponentRef(component)) return component;
    return {
      ...component,
      values: mapValues(component.values as Record<string, unknown>, component.componentId),
    };
  });
  const children = page.children?.map((child): WebsitePage => mapPageComponentsDeep(child, mapValues));
  return {
    ...page,
    components,
    ...(children !== undefined ? { children } : {}),
  };
}

export function resolveBindings(
  config: WebsiteConfig,
  data: ProfessionalBindingData,
  linkData?: LinkBindingData,
): WebsiteConfig {
  return {
    ...config,
    // Resolve shared components
    sharedComponents: config.sharedComponents
      ? Object.fromEntries(
          Object.entries(config.sharedComponents).map(([key, comp]) => [
            key,
            { ...comp, values: resolveValuesRecord(comp.values as Record<string, unknown>, data, linkData) },
          ]),
        )
      : undefined,
    pages: config.pages.map(
      (page): WebsitePage => mapPageComponentsDeep(page, (values) => resolveValuesRecord(values, data, linkData)),
    ),
    siteConfig: config.siteConfig
      ? (resolveValueDeep(config.siteConfig, data, linkData) as WebsiteConfig['siteConfig'])
      : undefined,
  };
}

// ─── Public resolver (masks protected bindings) ───────────────────────

const PROTECTED_HREF_PATTERN = /href="(mailto|tel):\{\{([a-z0-9_:-]+)\}\}"/g;

function applyProtectedHref(html: string): string {
  return html.replace(PROTECTED_HREF_PATTERN, (match, prefix: string, key: string) => {
    if (!isProtectedBinding(key)) return match;
    return `href="#" data-protect-href="${key}" data-protect-prefix="${prefix}:"`;
  });
}

function applyProtectedText(html: string): string {
  BINDING_PATTERN.lastIndex = 0;
  return html.replace(BINDING_PATTERN, (match, key: string) => {
    if (!isProtectedBinding(key)) return match;
    const field = BINDABLE_FIELDS[key];
    const placeholder = field?.protectedPlaceholder ?? '••••••';
    return `<span data-protect="${key}">${placeholder}</span>`;
  });
}

function maskProtectedBindings(value: string): string {
  if (!isBinding(value)) return value;
  return applyProtectedText(applyProtectedHref(value));
}

function maskValueDeepWith(val: unknown, maskString: (s: string) => string): unknown {
  if (typeof val === 'string') return maskString(val);
  if (Array.isArray(val)) return val.map((item: unknown): unknown => maskValueDeepWith(item, maskString));
  if (val !== null && typeof val === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      result[k] = maskValueDeepWith(v, maskString);
    }
    return result;
  }
  return val;
}

function maskProtectedBindingsPlain(value: string): string {
  if (!isBinding(value)) return value;
  BINDING_PATTERN.lastIndex = 0;
  return value.replace(BINDING_PATTERN, (match, key: string): string => {
    if (!isProtectedBinding(key)) return match;
    const field = BINDABLE_FIELDS[key];
    return field?.protectedPlaceholder ?? '••••••';
  });
}

const maskValueDeep = (val: unknown): unknown => maskValueDeepWith(val, maskProtectedBindings);
const maskValueDeepPlain = (val: unknown): unknown => maskValueDeepWith(val, maskProtectedBindingsPlain);

/**
 * Whether a protected binding in `field` may be masked with the `<span data-protect>` HTML
 * marker rather than a bare placeholder. The marker is DOM markup: it only works on fields
 * the renderer injects as HTML (`dangerouslySetInnerHTML`, i.e. `input_type: 'rich_text'`)
 * or that the component parses itself (`meta.binding_keys` — floating-contact reads the
 * marker to build its inert anchor). On a plain-text field React escapes the string, so the
 * marker would be DISPLAYED to visitors as literal `<span data-protect="pro:phone">…</span>`
 * (bug seen on `quote.authorRole`); those fields get the bare placeholder instead.
 */
function fieldAcceptsHtmlMarker(componentId: string | undefined, field: string): boolean {
  if (!componentId) return false;
  const schema = COMPONENT_SCHEMA_REGISTRY[componentId as ComponentId]?.schema as
    | { properties?: Record<string, { meta?: { input_type?: string; binding_keys?: unknown } }> }
    | undefined;
  const meta = schema?.properties?.[field]?.meta;
  if (!meta) return false;
  return meta.input_type === 'rich_text' || Array.isArray(meta.binding_keys);
}

function maskValuesRecord(values: Record<string, unknown>, componentId?: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(values)) {
    result[key] = fieldAcceptsHtmlMarker(componentId, key) ? maskValueDeep(val) : maskValueDeepPlain(val);
  }
  return result;
}

/**
 * Same as resolveBindings but replaces protected bindings (pro:email, pro:phone)
 * with DOM markers that ContactReveal resolves client-side via the captcha-protected
 * reveal endpoint. Use this on the public SSR path so protected values never reach
 * the static HTML.
 */
export function resolveBindingsPublic(
  config: WebsiteConfig,
  data: ProfessionalBindingData,
  linkData?: LinkBindingData,
): WebsiteConfig {
  const masked: WebsiteConfig = {
    ...config,
    sharedComponents: config.sharedComponents
      ? Object.fromEntries(
          Object.entries(config.sharedComponents).map(([key, comp]) => [
            key,
            { ...comp, values: maskValuesRecord(comp.values as Record<string, unknown>, comp.componentId) },
          ]),
        )
      : undefined,
    pages: config.pages.map(
      (page): WebsitePage =>
        mapPageComponentsDeep(page, (values, componentId) => maskValuesRecord(values, componentId)),
    ),
    // siteConfig is rendered as plain-text metadata (title/description) — mask protected
    // bindings to their placeholder string, without HTML wrappers that would break <title>.
    siteConfig: config.siteConfig ? (maskValueDeepPlain(config.siteConfig) as WebsiteConfig['siteConfig']) : undefined,
  };
  return resolveBindings(masked, data, linkData);
}

/**
 * Canonical page ids used as default link-binding targets in seeded component values
 * (see `ComponentSchemaRegistry` defaults + `default-website-config.json`, e.g. the
 * "Nous contacter" CTA → `{{page:page_contact}}`). On sites where the real page carries
 * a different id — v1-migrated sites get random ids (`page-xxxxxx`), and a pro can delete
 * then recreate a page — these tokens would not resolve by id and the CTA taps into the
 * void. We map each canonical id to the page TYPE it represents so the binding can fall
 * back to the matching page and always land somewhere sensible.
 */
const CANONICAL_PAGE_ID_TO_TYPE: Record<string, PageTypeEnum> = {
  page_contact: PageTypeEnum.CONTACT,
  contact: PageTypeEnum.CONTACT, // lead-preview template uses the bare id
  page_services_main: PageTypeEnum.SERVICES,
  page_booking: PageTypeEnum.BOOKING,
  page_blog: PageTypeEnum.BLOG,
};

/** Page node as seen by the binding map: id + slug, with recursive `children`. */
interface PageBindingNode {
  id: string;
  slug: string;
  type?: string;
  children?: PageBindingNode[];
}

/**
 * Joins a parent public path with a child slug, mirroring `joinPagePath` from
 * `find-page.ts` (shared cannot depend on shared-components). Strips the parent's
 * trailing slash and the child's leading slash before joining with a single `/`.
 */
function joinBindingPath(parentPath: string, childSlug: string): string {
  const base = (parentPath || '/').replace(/\/+$/, '');
  const leaf = (childSlug || '').replace(/^\/+/, '');
  if (!leaf) return parentPath || '/';
  return `${base}/${leaf}`;
}

/** Flat list of `{ id, type, path }` for every page in the tree, with full nested paths. */
function flattenPageBindingNodes(
  pages: PageBindingNode[],
  parentPath = '',
): Array<{ id: string; type?: string; path: string }> {
  const flat: Array<{ id: string; type?: string; path: string }> = [];
  for (const page of pages) {
    // Root pages keep their raw slug (the home page's empty-string slug is a valid
    // resolution → `baseUrl`, and must not become `/`). Sub-pages join onto their parent.
    const path = parentPath ? joinBindingPath(parentPath, page.slug) : page.slug;
    flat.push({ id: page.id, type: page.type, path });
    if (page.children?.length) flat.push(...flattenPageBindingNodes(page.children, path));
  }
  return flat;
}

/**
 * Build the `{ pageId → full path }` map used to resolve `{{page:…}}` link bindings, with
 * type-based fallbacks for the canonical default ids above. The tree is flattened so
 * sub-pages (`children`) resolve to their **full nested path** — otherwise a button
 * linking to a sub-page keeps a literal `{{page:…}}` token that the browser appends to
 * the current URL. A direct id match always wins; the fallback only fills a canonical id
 * that no page literally owns, so an unresolved "Contact" CTA reaches the contact page
 * instead of a dead link.
 */
export function buildPageBindingMap(pages: PageBindingNode[]): Record<string, string> {
  const flat = flattenPageBindingNodes(pages);
  const map: Record<string, string> = {};
  for (const page of flat) {
    map[page.id] = page.path;
  }
  for (const [canonicalId, type] of Object.entries(CANONICAL_PAGE_ID_TO_TYPE)) {
    if (map[canonicalId]) continue;
    const byType = flat.find((p): boolean => p.type === type);
    if (byType) map[canonicalId] = byType.path;
  }
  return map;
}

/**
 * Full public path of the first page of `type` in the tree, sub-pages included.
 * Used to build category/prestation/news URLs (`{servicesPagePath}/{categorySlug}`):
 * a services or blog page is very often a SUB-page (v1-migrated sites nest them under a
 * parent section), and a root-only lookup silently fell back to a hardcoded `/prestations`
 * that doesn't exist on those sites → link bindings resolved to a 404.
 */
export function findPagePathByType(pages: PageBindingNode[], type: string): string | undefined {
  return flattenPageBindingNodes(pages).find((p): boolean => p.type === type)?.path;
}

export function buildLinkBindingDataFromConfig(config: WebsiteConfig): LinkBindingData {
  return { pages: buildPageBindingMap(config.pages), categories: {}, prestations: {} };
}

function extractBindingKeysDeep(val: unknown, keys: Set<string>): void {
  if (typeof val === 'string') {
    BINDING_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = BINDING_PATTERN.exec(val)) !== null) {
      const key = match[1];
      if (isLinkBinding(key)) keys.add(key);
    }
  } else if (Array.isArray(val)) {
    for (const item of val) extractBindingKeysDeep(item, keys);
  } else if (val !== null && typeof val === 'object') {
    for (const v of Object.values(val as Record<string, unknown>)) extractBindingKeysDeep(v, keys);
  }
}

/** Scans component values and returns all link-binding keys (e.g. 'prestation:123', 'news:5'). */
export function extractLinkBindingKeys(valuesArray: Record<string, unknown>[]): string[] {
  const keys = new Set<string>();
  for (const values of valuesArray) {
    for (const val of Object.values(values)) {
      extractBindingKeysDeep(val, keys);
    }
  }
  return Array.from(keys);
}

/** Every component of the config — shared ones, root pages AND sub-pages (`children`). */
function forEachComponentValues(config: WebsiteConfig, visit: (values: Record<string, unknown>) => void): void {
  for (const comp of Object.values(config.sharedComponents ?? {})) {
    visit(comp.values as Record<string, unknown>);
  }
  const visitPage = (page: WebsitePage): void => {
    for (const component of page.components) {
      if (isComponentRef(component)) continue;
      visit(component.values as Record<string, unknown>);
    }
    for (const child of page.children ?? []) visitPage(child);
  };
  for (const page of config.pages) visitPage(page);
}

/**
 * True when the config still carries at least one link binding (`{{page|category|prestation|news:…}}`).
 * Used as a gate to skip the catalog DB round-trip needed to resolve them. The scan is DEEP
 * (a binding nested in an array of items counts) and covers sub-pages — a shallow scan
 * silently disabled the whole link-resolution pass for configs that only bind inside lists.
 */
export function configHasLinkBindings(config: WebsiteConfig): boolean {
  const keys = new Set<string>();
  forEachComponentValues(config, (values): void => extractBindingKeysDeep(values, keys));
  return keys.size > 0;
}

function resolveLinkValueDeep(val: unknown, linkData: LinkBindingData): unknown {
  if (typeof val === 'string') {
    if (!isBinding(val)) return val;
    BINDING_PATTERN.lastIndex = 0;
    return val.replace(BINDING_PATTERN, (match, key: string): string => {
      if (!isLinkBinding(key)) return match;
      // Unresolvable key (page/prestation deleted, catalog id renumbered) → keep the token
      // rather than emit an empty href, so the breakage stays visible and diagnosable.
      return resolveLinkBinding(key, linkData) ?? match;
    });
  }
  if (Array.isArray(val)) return val.map((item: unknown): unknown => resolveLinkValueDeep(item, linkData));
  if (val !== null && typeof val === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      result[k] = resolveLinkValueDeep(v, linkData);
    }
    return result;
  }
  return val;
}

/**
 * Resolves ONLY the link bindings of a config, leaving every other `{{...}}` untouched
 * (pro bindings are resolved in a first pass, before the catalog is even queried).
 * Walks shared components, root pages and sub-pages, deeply into arrays/objects.
 */
export function resolveLinkBindingsInConfig(config: WebsiteConfig, linkData: LinkBindingData): WebsiteConfig {
  const resolveValues = (values: Record<string, unknown>): Record<string, unknown> =>
    resolveLinkValueDeep(values, linkData) as Record<string, unknown>;

  return {
    ...config,
    sharedComponents: config.sharedComponents
      ? Object.fromEntries(
          Object.entries(config.sharedComponents).map(([key, comp]) => [
            key,
            { ...comp, values: resolveValues(comp.values as Record<string, unknown>) },
          ]),
        )
      : undefined,
    pages: config.pages.map((page): WebsitePage => mapPageComponentsDeep(page, resolveValues)),
  };
}
